import { parseISO } from "date-fns";
import { FC } from "react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Header } from "@/components/ui/header";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { SKU, Tag } from "@/data/types";

type Props = {
  tag?: Tag;
  sku?: SKU;
};

export const TagDetailsSection: FC<Props> = ({ sku, tag }) => (
  <div className="flex flex-col w-full">
    <Header title="Details" />
    <Card className="h-full mt-4">
      <CardContent className="my-6 px-4 mx-0 pb-0">
        <Table className="table-auto">
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Tag ID</TableCell>
              <TableCell className="w-full">{tag?.uid}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Programmed At</TableCell>
              <TableCell className="w-full">
                {tag?.created_at
                  ? parseISO(tag?.created_at).toLocaleString()
                  : ""}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">SKU ID</TableCell>
              <TableCell className="w-full">{sku?.id}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">SKU Name</TableCell>
              <TableCell className="w-full">{sku?.name}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Marketing Consent</TableCell>
              <TableCell className="w-full">
                {tag?.email_marketing_consent ? (
                  <Badge variant="green">Yes</Badge>
                ) : (
                  <Badge variant="secondary">No</Badge>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Owner Email</TableCell>
              <TableCell className="w-full">
                {tag?.owner_email ?? (
                  <span className="text-muted-foreground">No owner email</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Owner Wallet</TableCell>
              <TableCell className="w-full">
                {tag?.owner_address ?? (
                  <span className="text-muted-foreground">No owner</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Quality Check</TableCell>
              <TableCell className="w-full">
                {tag?.qc ? (
                  <Badge variant="green">Passed</Badge>
                ) : (
                  <Badge variant="orange">Pending</Badge>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Tamper Detection</TableCell>
              <TableCell className="w-full">
                {tag?.tamper_enabled ? (
                  <Badge>On</Badge>
                ) : (
                  <Badge variant="secondary">Off</Badge>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">
                Latest Tamper Status
              </TableCell>
              <TableCell className="w-full">
                {!tag?.tamper && <Badge variant="secondary">--</Badge>}
                {tag?.tamper === "CC" && (
                  <Badge variant="secondary">Sealed</Badge>
                )}
                {tag?.tamper === "OO" && <Badge>Opened</Badge>}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Page Views</TableCell>
              <TableCell className="w-full">
                {tag?.tag_view_verifications_count ?? (
                  <span className="text-muted-foreground">0</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">
                API Tag Verifications
              </TableCell>
              <TableCell className="w-full">
                {tag?.external_verifications ?? (
                  <span className="text-muted-foreground">0</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Ownership Claims</TableCell>
              <TableCell className="w-full">
                {tag?.claim_count ?? (
                  <span className="text-muted-foreground">0</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Ownership Resets</TableCell>
              <TableCell className="w-full">
                {tag?.reset_count ?? (
                  <span className="text-muted-foreground">0</span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Total Verifications</TableCell>
              <TableCell className="w-full">
                {tag?.verifications ?? (
                  <span className="text-muted-foreground">0</span>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  </div>
);
